import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

const MODULES = { spot: "Spot", p2p: "P2P", otc: "OTC", escrow: "Escrow" };

const inr = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ProfileTax() {
  const year = new Date().getFullYear();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/api/user/tds/summary/yearly", { params: { year } }),
      api.get("/api/user/tds/history", { params: { limit: 50 } }),
    ])
      .then(([s, h]) => {
        if (!alive) return;
        setSummary(s.data);
        setRows(h.data.items || []);
      })
      .catch((e) => alive && setError(getErrorMessage(e)));
    return () => {
      alive = false;
    };
  }, [year]);

  const byModule = summary?.by_module || {};

  return (
    <AppShell>
      <div className="space-y-6" data-testid="tax-tds-page">
        <div>
          <h1 className="text-2xl font-semibold">Tax & TDS</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Deductions follow the rates set in the admin panel. GST on platform fees is shown on the{" "}
            <Link to="/fees" className="text-primary hover:underline">fee schedule</Link>.
          </p>
        </div>

        {error && <p className="text-sm text-down">{error}</p>}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">TDS collected in {year}</p>
            <p className="num mt-2 text-lg font-semibold">{summary ? inr(summary.total_tds_inr) : "—"}</p>
          </div>
          <div className="rounded-xl border border-border bg-surface/50 p-5">
            <p className="text-xs text-muted-foreground">By product</p>
            {summary && Object.keys(byModule).length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">No deductions this year.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {Object.entries(byModule).map(([mod, row]) => (
                  <li key={mod} className="flex justify-between gap-3">
                    <span className="text-muted-foreground">{MODULES[mod] || mod}</span>
                    <span className="num">{inr(row.tds_inr)} · {row.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-end justify-between gap-3">
            <h2 className="text-lg font-semibold">Statement</h2>
            <a
              href={`/api/user/tds/history/export.csv?year=${year}`}
              className="text-xs text-primary hover:underline"
            >
              Download CSV
            </a>
          </div>
          {rows === null ? (
            <div className="skeleton mt-3 h-24" />
          ) : rows.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No TDS has been deducted on your account yet.</p>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-surface/40">
              <div className="min-w-[640px]">
                <div className="table-head grid grid-cols-[1fr_0.8fr_1fr_0.8fr_1fr] gap-3 px-4 py-2.5">
                  <span>When</span>
                  <span>Product</span>
                  <span>Trade</span>
                  <span className="text-right">Trade value</span>
                  <span className="text-right">TDS</span>
                </div>
                {rows.map((r) => (
                  <div key={r.tds_tx_id} className="terminal-row grid-cols-[1fr_0.8fr_1fr_0.8fr_1fr] gap-3 px-4 py-3 text-sm">
                    <span className="text-xs text-muted-foreground">{when(r.created_at)}</span>
                    <span>{MODULES[r.module] || r.module}</span>
                    <span className="truncate font-mono text-xs">{r.trade_id || "—"}</span>
                    <span className="num text-right">{inr(r.trade_amount)}</span>
                    <span className="num text-right">{inr(r.tds_amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
